<template>
  <div
    v-if="user && ($login.can('users.view') || $login.can('users.view.channel'))"
    class="users-view"
  >
    <page-header :header="user.name">
      <template slot="subtitle">
        <div class="mt-1 mr-2">
          {{ user.job_title }}
          <span v-if="user.account">
            <span v-if="user.job_title">
              @
            </span>
            <router-link
              :to="`/accounts/${user.account.id}`"
            >
              {{ user.account.name }}
            </router-link>
          </span>
        </div>
      </template>

      <button
        v-if="canEdit && $login.can('analytics.network')"
        class="d-none d-lg-flex mr-2 btn btn-primary"
        @click="generateExcel()"
      >
        Export to Excel
      </button>

      <button
        v-if="$login.can('users.ghost-in')"
        class="mr-2 btn btn-outline-primary"
        @click="ghost(user)"
      >
        Ghost in
      </button>
      <dropdown
        v-if="$login.can('users.update') || $login.can('users.ghost-in') || $login.can('users.admin-ghost-in')"
        trigger="click"
        class="no-chev"
        alignment="right"
      >
        <button slot="trigger" class="btn btn-outline-primary btn--more">
          <icon icon="more-vertical" />
        </button>
        <dropdown-option
          v-if="canEdit && $login.can('analytics.network')"
          class="d-lg-none"
          @click.native="generateExcel()"
        >
          <icon
            icon="folder"
            class="mr-2"
          /> Export to Excel
        </dropdown-option>

        <dropdown-option
          v-if="$login.can('users.update')"
          class="page-header-dropdown-item"
          @click.native="sendForgotTo(user)"
        >
          <icon icon="password" /> Send forgot password
        </dropdown-option>

        <dropdown-option
          v-if="$login.can('users.update') && !user.agreed_to_terms_of_use_at && false"
          class="page-header-dropdown-item"
          @click.native="sendWelcomeTo(user)"
        >
          <icon icon="welcome" /> Send Welcome email
        </dropdown-option>

        <dropdown-option
          v-if="$login.can('users.update') && !user.is_gdpr_compliant && false"
          class="page-header-dropdown-item"
          @click.native="sendGdprTo(user)"
        >
          <icon icon="delivered" /> Send GDPR email
        </dropdown-option>

        <dropdown-option
          v-if="$login.can('users.update') && !user.is_activated"
          class="page-header-dropdown-item"
          @click.native="sendInviteTo(user)"
        >
          <icon icon="delivered" /> Invite to Insights {{ user.permissions.length > 0 ? 'Pro' : 'Starter' }}
        </dropdown-option>

        <dropdown-option
          v-if="$login.can('users.ghost-in')"
          class="page-header-dropdown-item"
          @click.native="ghost(user)"
        >
          <icon icon="ghost" /> Ghost in
        </dropdown-option>
        <dropdown-option
          v-if="$login.can('users.admin-ghost-in')"
          class="page-header-dropdown-item"
          @click.native="ghost(user, true)"
        >
          <icon icon="ghost" /> Admin Ghost in
        </dropdown-option>

        <dropdown-option
          v-if="canEdit && $login.can('analytics.network')"
          class="page-header-dropdown-item"
          @click.native="generateExcel('this_365_days')"
        >
          <icon
            icon="folder"
            class="mr-2"
          /> Export last 365 days to Excel
        </dropdown-option>

      </dropdown>
    </page-header>
    <tabs>
      <!-- Analytics tab -->
      <tab
        v-if="$login.canAnalytics()"
        name="Analytics"
      >
        <analytics
          :data="user"
          :channel-name="$login.can('analytics.channel') && $login.getChannel() ? $login.getChannelName() : 'Network'"
        />
      </tab>

      <!-- Activity tab -->
      <tab
        name="Activity"
        v-if="$login.can('users.audit')"
      >
        <activity
          :data="user"
          type="user"
        />
      </tab>

      <!-- Details tab -->
      <tab
        v-if="canEdit"
        name="Details"
        class="pt-3"
      >
        <edit-user
          :user="user"
          :alert-lists="alertLists"
          :all-alert-lists="allAlertLists"
        />

      </tab>

      <!-- Details tab -->
      <tab
        name="Follows"
        class="pt-3"
      >
        <follows :user="user" />
      </tab>
    </tabs>

    <confirm
      v-if="sendWelcomeToUser"
      modal="confirm-send-welcome"
      @confirmed="sendWelcome"
    >
      Are you sure you want to send a welcome email to {{ sendWelcomeToUser.name }}?
    </confirm>

    <confirm
      v-if="sendForgotUser"
      modal="confirm-send-forgot"
      @confirmed="sendForgot"
    >
      Are you sure you want to send a forgot password email to {{ sendForgotUser.name }}?
    </confirm>

    <confirm
      v-if="sendGdprToUser"
      modal="confirm-send-gdpr"
      @confirmed="sendGdpr"
    >
      Are you sure you want to send a GDPR email to {{ sendGdprToUser.name }}?
    </confirm>

    <confirm
      v-if="sendInviteToUser"
      modal="confirm-send-invite"
      @confirmed="sendInvite"
    >
      Are you sure you want to send an invite to {{ sendInviteToUser.name }}?
    </confirm>
  </div>
</template>

<script>
import axios from '@/axios'
import Tabs from '@/components/Tabs'
import Tab from '@/components/Tab'
import Analytics from './Analytics'
import Activity from '@/components/Activity'
import EditUser from './Edit'
import ReadOnly from './ReadOnly'
import Confirm from '@/components/Confirm'
import mixin from './Mixin'
import {successNotification} from '@/notifications'
import Follows from './Follows'

import Dropdown from '@/components/Dropdown/Dropdown'
import DropdownOption from '@/components/Dropdown/DropdownOption'

export default {
  mixins: [mixin],
  components: {
    Tabs,
    Tab,
    Analytics,
    Activity,
    EditUser,
    ReadOnly,
    Dropdown,
    DropdownOption,
    Follows,
    Confirm,
  },

  metaInfo () {
    return {
      title: this.user ? this.user.name : 'View User',
    }
  },

  data () {
    return {
      user: null,
      loading: false,
      alertLists: null,
      error: null,
      allAlertLists: [],
      saving: false,
      audit: [],
    }
  },

  computed: {
    disableTabs () {
      return [!this.$login.can('audit.users') ? 'Activity' : null]
    },
    canEdit () {
      return this.$login.can('users.update')
    },
  },

  created () {
    const {userId} = this.$route.params
    this.getAllData(userId)
  },

  methods: {
    async getAllData (userId) {
      const [
        userResponse,
        alertListsResponse,
        allAlertListsResponse,
      ] = await Promise.all([
        axios.get(`/users/${userId}?populate[]=account&populate[]=country&populate[]=userRole&populate[]=channels&populate[]=roles&populate[]=allRoles&populate[]=allSubscriptions.roles.permissions.entitlement&append[]=permissions`),
        axios.get(`/users/${userId}/alert-lists`),
        axios.get('/alert-lists'),
      ])

      this.user = userResponse.data.data
      this.alertLists = alertListsResponse.data.data
      this.allAlertLists = allAlertListsResponse.data.data
    },

    json (what) {
      return JSON.stringify(what, null, 2)
    },

    updatedUser (response) {
      this.$notify(successNotification('Updated user successfully'))
      this.user = response.data.data
    },

    async generateExcel (timeframe=this.$timeframe) {
      const currentUser = this.$login.getUser()
      try {
        this.$http.post(`/reports/user/${this.user.id}?timeframe=${timeframe}&user_id=${currentUser.id}`)
        this.$notify(successNotification(`A report download link will be emailed to ${currentUser.email} as soon as it has been generated, typically within 5 minutes`))
      } catch (e) {
        console.log(e)
      }
    },
  },
}
</script>

<style lang="scss">
.users-view {
  .card-header {
    padding: 0;
    .filter-buttons {
      display: flex;
      margin-right: auto;
      .btn {
        width: 120px;
        flex: 1;
      }
    }

    .el-input {
      margin-left: 24px;
      max-width: 240px;

      &__inner {
        padding: 16px;
        text-align: center;
      }
    }
  }
 .clear-all-alerts:hover {
    .btn {
      text-decoration: underline;
      color: $primary;
    }
  }
}

.card-body {
  padding: 0;
  .el-table {
    &__header {
      th {
        background: #FBFBFB;
      }
    }
  }
}

</style>
